import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, of, Subject } from 'rxjs'
import { AccessPassCard, CardsOfUserApiResponse, Fridge } from '../types/mpup-types'
import { catchError, finalize, takeUntil } from 'rxjs/operators'
import { MpupPaginationService } from './mpup-pagination.service'
import { MpupAuthorizationService } from './mpup-authorization.service'
import { HttpErrorResponse } from '@angular/common/http'
import { environment } from '../../../../environments/environment'

export const MPUP_ACCESS_PASSES_ENDPOINT = environment.mpup.baseUrl + '/MPUP/cardsOfUser'

@Injectable({
  providedIn: 'root'
})
export class MpupAccessPassesService extends MpupPaginationService implements OnDestroy {

  private countryCodeSubject$ = new BehaviorSubject('DE') // German contact as fallback ?
  private cards$ = new BehaviorSubject<ReadonlyArray<AccessPassCard>>([])
  private fridges$ = new BehaviorSubject<ReadonlyArray<Fridge>>([])
  private loading$ = new BehaviorSubject(true)
  private unsubscribe$ = new Subject<void>()

  constructor (private authorizationService: MpupAuthorizationService) {
    super()
    this.fetchAccessPasses()
    this.fridges$.subscribe({
      next: (fridges) => {
        if (fridges.length > 0) {
          this.countryCodeSubject$.next(fridges[0].country)
        } else {
          console.warn('No fridges received, can not infer region code. Defaulting to german.')
        }
      }
    })
  }

  ngOnDestroy () {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  fetchAccessPasses () {
    this.authorizationService.authorizedRequest<CardsOfUserApiResponse>(MPUP_ACCESS_PASSES_ENDPOINT)
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError((error) => this.handleError(error)),
        finalize(() => this.loading$.next(false))
      )
      .subscribe({
        next: (response) => {
          this.cards$.next(response.cardData.item.map(card => ({ ...card, active: card.active === 'X' })))
          this.fridges$.next(response.fridgeData.item
            // this can happen because the cpi is unable to send an empty array
            // @ts-ignore
            .filter(f => f !== '')
            .map(fridge => ({ ...fridge, active: fridge.active === 'X' })))
        }
      })
  }

  getLoading (): Observable<boolean> {
    return this.loading$.asObservable()
  }

  getAccessPasses (): Observable<ReadonlyArray<AccessPassCard>> {
    return this.cards$.asObservable()
  }

  getFridges (): Observable<ReadonlyArray<Fridge>> {
    return this.fridges$.asObservable()
  }

  getCountryCode (): Observable<string> {
    return this.countryCodeSubject$.asObservable()
  }

  handleError (response: HttpErrorResponse): Observable<CardsOfUserApiResponse> {
    const errorCode = response.status
    const errorMessage = response.message || response.error.message
    console.error(`Http request failed. status: ${errorCode} - message: ${errorMessage}`)

    return of({
      cardData: { item: [] },
      fridgeData: { item: [] },
      fridgeDataAdd: { item: [] }
    })
  }
}
